import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight,
  CheckCircle2,
  Shield,
  Star,
  MapPin,
  Wrench,
  Home,
  Droplets,
  CloudLightning,
} from "lucide-react";
import { SEOHead } from "@/components/SEOHead";
import Calculator from "@/components/Calculator";

const homepageSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://www.saskroofingcompany.com/#organization",
      name: "Sask Roofing Company",
      url: "https://www.saskroofingcompany.com/",
      logo: "https://www.saskroofingcompany.com/favicon.svg",
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+1-639-739-7969",
        contactType: "customer service",
        email: "info@saskroofingcompany.com",
        areaServed: "CA-SK",
        availableLanguage: "English",
      },
    },
    {
      "@type": "WebSite",
      "@id": "https://www.saskroofingcompany.com/#website",
      url: "https://www.saskroofingcompany.com/",
      name: "Sask Roofing Company",
      publisher: { "@id": "https://www.saskroofingcompany.com/#organization" },
      inLanguage: "en-CA",
    },
    {
      "@type": "RoofingContractor",
      "@id": "https://www.saskroofingcompany.com/#localbusiness",
      name: "Sask Roofing Company",
      alternateName: ["Sask Roofing Co", "Sask Roofing"],
      parentOrganization: {
        "@id": "https://www.saskroofingcompany.com/#organization",
      },
      image:
        "https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/918ec229-07a1-46df-81c2-872eb46770de.png&output=webp&q=60",
      logo: { "@id": "https://www.saskroofingcompany.com/#organization" },
      telephone: "+1-639-739-7969",
      email: "info@saskroofingcompany.com",
      url: "https://www.saskroofingcompany.com/",
      address: {
        "@type": "PostalAddress",
        addressRegion: "SK",
        addressCountry: "CA",
      },
      areaServed: [
        {
          "@type": "City",
          name: "Saskatoon",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Regina",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Moose Jaw",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Prince Albert",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Swift Current",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Yorkton",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Estevan",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Weyburn",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "North Battleford",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Lloydminster",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
      ],
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          opens: "08:00",
          closes: "18:00",
        },
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: "Saturday",
          opens: "09:00",
          closes: "14:00",
        },
      ],
      priceRange: "$$",
      paymentAccepted: "Cash, Credit Card, Financing",
      currenciesAccepted: "CAD",
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.9",
        reviewCount: "87",
        bestRating: "5",
        worstRating: "1",
      },
    },
  ],
};

const calculatorSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Sask Roofing Company Cost Calculator",
  url: "https://www.saskroofingcompany.com/calculator",
  description:
    "Free instant estimate of roof replacement cost based on Saskatchewan market rates. Adjust home size, material, pitch, and tear-off to get a personalized range.",
  applicationCategory: "FinanceApplication",
  operatingSystem: "Any (web browser)",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "CAD",
  },
  provider: { "@id": "https://www.saskroofingcompany.com/#localbusiness" },
};

export default function Index() {
  return (
    <div className="flex flex-col min-h-screen">
      <SEOHead
        title="Roofing Company Saskatchewan | Sask Roofing Company"
        description="Saskatchewan-owned roofing company serving Saskatoon, Regina, and province-wide. Local crews, roofs built for our climate. Get a free inspection."
        canonical="/"
        schemaJson={[homepageSchema, calculatorSchema]}
      />
      {/* Hero Section */}
      <section className="relative w-full py-24 md:py-32 lg:py-40 bg-black overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/a064f76a-cb98-4f5b-8cb3-4a80f8281705.png&output=webp&q=60&w=1920"
            alt="Beautiful newly installed roof in Saskatchewan"
            className="w-full h-full object-cover"
            fetchPriority="high"
            width={1920}
            height={1080}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/10 to-transparent" />
        </div>

        <div className="container relative z-10 px-4 md:px-6">
          <div className="max-w-3xl space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-primary-foreground leading-tight">
              Saskatchewan's Best Roofing Company
            </h1>
            <p className="text-lg md:text-xl text-primary-foreground/90 max-w-2xl leading-relaxed">
              Sask Roofing Company is the trusted local roofing company in
              Saskatchewan. We build roofs designed to survive Saskatchewan's
              brutal winters, heavy snow loads, and summer hailstorms. Honest,
              fixed pricing in writing with local crews you can trust.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6 h-auto"
                asChild
              >
                <Link to="/contact">Get Your Free Roof Inspection</Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20 text-lg px-8 py-6 h-auto"
                asChild
              >
                <a href="tel:6397397969">(639) 739-7969</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Bar */}
      <section className="bg-primary text-primary-foreground py-6 border-b border-primary-foreground/10">
        <div className="container px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center justify-center p-4">
              <span className="text-4xl md:text-5xl font-bold text-accent mb-2">
                4.9/5
              </span>
              <span className="text-sm md:text-base font-medium text-primary-foreground/80">
                Average Rating
              </span>
            </div>
            <div className="flex flex-col items-center justify-center p-4">
              <span className="text-4xl md:text-5xl font-bold text-accent mb-2">
                15+
              </span>
              <span className="text-sm md:text-base font-medium text-primary-foreground/80">
                Years in Business
              </span>
            </div>
            <div className="flex flex-col items-center justify-center p-4">
              <span className="text-4xl md:text-5xl font-bold text-accent mb-2">
                2,500+
              </span>
              <span className="text-sm md:text-base font-medium text-primary-foreground/80">
                Roofs Completed
              </span>
            </div>
            <div className="flex flex-col items-center justify-center p-4">
              <span className="text-4xl md:text-5xl font-bold text-accent mb-2">
                100%
              </span>
              <span className="text-sm md:text-base font-medium text-primary-foreground/80">
                Saskatchewan-Owned
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Problem / Pain Section */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                Generic Roofs Fail in <br />
                Saskatchewan Weather.
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Between extreme winter-to-summer temperature swings (-40°C to
                +35°C), severe ice damming, high prairie winds, and our infamous
                hail corridor, a standard builder-grade roof just isn't enough.
                When cheap shingles blow off or hail compromises your roof, it
                leads to hidden leaks, costly energy loss, and severe structural
                damage. You need a roofing system designed for the realities of
                our climate.
              </p>
              <p className="text-lg font-medium text-foreground border-l-4 border-accent pl-4">
                We fix that. Our roofing systems are specifically chosen and
                installed to withstand Saskatchewan conditions. We use high-wind
                rated architectural shingles and premium underlayments, ensuring
                your home is protected year-round. No cut corners, no generic
                solutions.
              </p>
              <ul className="space-y-3 pt-2">
                {[
                  "Expert hail damage repair & storm restoration",
                  "High-wind rated architectural shingles",
                  "Proper ventilation to prevent ice damming",
                ].map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-muted-foreground"
                  >
                    <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/a06f0735-dc69-4346-b3b4-960c40ceb8f1.png&output=webp&q=60&w=800"
                alt="House under a stormy Saskatchewan prairie sky demonstrating the need for weather-resistant roofing"
                className="w-full h-[500px] object-cover"
                loading="lazy"
                width={800}
                height={500}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 md:py-28 bg-muted/50">
        <div className="container px-4">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Saskatchewan Roofing Services
            </h2>
            <p className="text-lg text-muted-foreground">
              From minor repairs to complete replacements, our local crews
              deliver premium craftsmanship backed by real warranties.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Category 1: Replacement */}
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow bg-background overflow-hidden flex flex-col group">
              <div className="h-48 overflow-hidden">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/04b10f0a-3e4a-4a6e-a03b-9f26ff276b90.png&output=webp&q=60&w=600"
                  alt="Roof Replacement & Installation"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  width={600}
                  height={400}
                />
              </div>
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Home className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="text-xl">
                  Replacement & Installation
                </CardTitle>
                <CardDescription className="text-base">
                  Built to last decades.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 flex-1 flex flex-col">
                <ul className="space-y-2 text-muted-foreground flex-1">
                  <li>• Asphalt Shingle Roofing</li>
                  <li>• Premium Metal Roofing</li>
                  <li>• Complete Tear-offs</li>
                </ul>
                <Button
                  variant="link"
                  className="p-0 h-auto text-primary font-bold group/btn justify-start"
                  asChild
                >
                  <Link to="/services/replacement">
                    Learn about Replacement{" "}
                    <ArrowRight
                      className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform"
                      aria-hidden="true"
                    />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* Category 2: Repair */}
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow bg-background overflow-hidden flex flex-col group">
              <div className="h-48 overflow-hidden">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/a45bf747-d0d6-427b-adfb-d9ba7d5beb9b.png&output=webp&q=60&w=600"
                  alt="Roof Repair & Restoration"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  width={600}
                  height={400}
                />
              </div>
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Wrench className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="text-xl">Repair & Restoration</CardTitle>
                <CardDescription className="text-base">
                  Fast response when you need it.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 flex-1 flex flex-col">
                <ul className="space-y-2 text-muted-foreground flex-1">
                  <li>• Leak Diagnosis & Roof Repair</li>
                  <li>• Storm & Hail Damage Repair</li>
                  <li>• Comprehensive Roof Inspections</li>
                </ul>
                <Button
                  variant="link"
                  className="p-0 h-auto text-primary font-bold group/btn justify-start"
                  asChild
                >
                  <Link to="/services/repair">
                    Learn about Repair{" "}
                    <ArrowRight
                      className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform"
                      aria-hidden="true"
                    />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* Category 3: Commercial & Add-ons */}
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow bg-background overflow-hidden flex flex-col group">
              <div className="h-48 overflow-hidden">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/b36d45df-4111-4a48-a8f9-5b1eb8b63f52.png&output=webp&q=60&w=600"
                  alt="Commercial & Exteriors"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  width={600}
                  height={400}
                />
              </div>
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Droplets className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="text-xl">
                  Commercial & Exteriors
                </CardTitle>
                <CardDescription className="text-base">
                  Complete building protection.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 flex-1 flex flex-col">
                <ul className="space-y-2 text-muted-foreground flex-1">
                  <li>• Flat & Commercial Roofing</li>
                  <li>• Eavestrough & Gutters</li>
                  <li>• Custom Flashings</li>
                </ul>
                <Button
                  variant="link"
                  className="p-0 h-auto text-primary font-bold group/btn justify-start"
                  asChild
                >
                  <Link to="/services/commercial">
                    Learn about Commercial{" "}
                    <ArrowRight
                      className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform"
                      aria-hidden="true"
                    />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Material Comparison Table */}
          <div className="mt-20 max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h3 className="text-2xl md:text-3xl font-bold text-foreground">
                Roofing Options at a Glance
              </h3>
              <p className="text-muted-foreground mt-2">
                Compare our core services to find the right fit for your home.
              </p>
            </div>
            <div className="space-y-8">
              <div className="overflow-x-auto rounded-xl border bg-background shadow-sm">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-muted/50 border-b">
                      <th className="p-4 font-bold text-foreground w-1/4">
                        Service
                      </th>
                      <th className="p-4 font-bold text-foreground">
                        Cost Profile
                      </th>
                      <th className="p-4 font-bold text-foreground">
                        Lifespan
                      </th>
                      <th className="p-4 font-bold text-foreground">
                        Climate Fit
                      </th>
                      <th className="p-4 font-bold text-foreground">
                        Warranty
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr className="hover:bg-muted/20 transition-colors">
                      <td className="p-4 font-medium">Asphalt Shingles</td>
                      <td className="p-4 text-muted-foreground">$$</td>
                      <td className="p-4 text-muted-foreground">20-30 Years</td>
                      <td className="p-4 text-muted-foreground">
                        Excellent (Wind/Hail Rated)
                      </td>
                      <td className="p-4 text-muted-foreground">
                        Up to 50 Years
                      </td>
                    </tr>
                    <tr className="hover:bg-muted/20 transition-colors">
                      <td className="p-4 font-medium">Metal Roofing</td>
                      <td className="p-4 text-muted-foreground">$$$$</td>
                      <td className="p-4 text-muted-foreground">50+ Years</td>
                      <td className="p-4 text-muted-foreground">
                        Superior (Sheds Snow/Ice)
                      </td>
                      <td className="p-4 text-muted-foreground">Lifetime</td>
                    </tr>
                    <tr className="hover:bg-muted/20 transition-colors">
                      <td className="p-4 font-medium">Roof Repair</td>
                      <td className="p-4 text-muted-foreground">$</td>
                      <td className="p-4 text-muted-foreground">
                        Extends Life
                      </td>
                      <td className="p-4 text-muted-foreground">
                        Targeted Fixes
                      </td>
                      <td className="p-4 text-muted-foreground">Workmanship</td>
                    </tr>
                    <tr className="hover:bg-muted/20 transition-colors">
                      <td className="p-4 font-medium">Roof Inspection</td>
                      <td className="p-4 text-muted-foreground">Free</td>
                      <td className="p-4 text-muted-foreground">N/A</td>
                      <td className="p-4 text-muted-foreground">
                        Preventative
                      </td>
                      <td className="p-4 text-muted-foreground">N/A</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="rounded-xl overflow-hidden border bg-background shadow-sm w-full">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/b00247a9-8f31-46f8-b717-07b8061a85fb.png&output=webp&q=60&w=1200"
                  alt="Premium roofing materials comparison"
                  className="w-full h-auto object-cover aspect-video md:aspect-[21/9]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
              Our Proven 4-Step Process
            </h2>
            <p className="text-lg text-muted-foreground">
              Getting a new roof shouldn't be stressful. We make it
              straightforward and transparent.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto mt-8">
            {[
              {
                step: "01",
                title: "Free Roof Inspection",
                desc: "We thoroughly assess your roof's condition, looking for storm damage, wear, and ventilation issues. You get a same-day honest diagnosis.",
                img: "https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/79619f96-c06c-4bc8-8f59-77a613fb7287.png&output=webp&q=60&w=400",
              },
              {
                step: "02",
                title: "Custom Quote & Selection",
                desc: "You get a detailed, fixed-price quote in writing. No hidden fees. We help you choose the best materials for your specific home and our Saskatchewan climate.",
                img: "https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/92965182-6dca-4b26-9425-b7213db681d6.png&output=webp&q=60&w=400",
              },
              {
                step: "03",
                title: "Professional Installation",
                desc: "Our local crew installs your new roof efficiently, typically in 1-2 days. We protect your landscaping and clean up completely with a magnetic sweep.",
                img: "https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/f3935cc0-f115-45f7-a9e9-2268ff84839d.png&output=webp&q=60&w=400",
              },
              {
                step: "04",
                title: "Final Walkthrough",
                desc: "We walk the property with you to ensure 100% satisfaction. You receive your written warranty documents for both workmanship and materials.",
                img: "https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/b475b11e-2ed3-4972-913f-5009458f567e.png&output=webp&q=60&w=400",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-background p-6 rounded-xl border shadow-sm flex flex-col relative mt-6"
              >
                <div className="absolute -top-5 left-6 flex items-center justify-center w-10 h-10 rounded-full border-4 border-background bg-primary text-primary-foreground font-bold shadow z-10">
                  {item.step}
                </div>
                <img
                  src={item.img}
                  alt={item.title}
                  className="w-full h-40 rounded-lg object-cover shadow-sm mb-6 mt-2"
                  loading="lazy"
                  width={400}
                  height={300}
                />
                <h3 className="text-xl font-bold text-foreground mb-3">
                  {item.title}
                </h3>
                <p className="text-muted-foreground flex-1">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-28 bg-primary text-primary-foreground">
        <div className="container px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/2de89809-eea4-46a6-aaf9-fe65daf5aa25.png&output=webp&q=60&w=800"
                alt="Close-up of professional roofing craftsmanship on premium asphalt shingles"
                className="rounded-2xl shadow-2xl"
                loading="lazy"
                width={800}
                height={600}
              />
            </div>
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
                  Why Saskatchewan Homeowners Choose Us
                </h2>
                <p className="text-lg text-primary-foreground/80">
                  Unlike national chains that rely on rotating subcontractors,
                  we are a local business. Our reputation is built on doing the
                  job right the first time.
                </p>
              </div>

              <div className="space-y-6">
                {[
                  {
                    title: "Honest, Fixed Pricing",
                    desc: "The price on your contract is the price you pay. No 'unforeseen extras' halfway through. We've never charged a customer a single dollar over their quote.",
                  },
                  {
                    title: "Climate-Tested Materials",
                    desc: "We only install products proven to handle Saskatchewan's extreme weather. From high-wind architectural shingles to premium ice and water shields.",
                  },
                  {
                    title: "Local, Experienced Crews",
                    desc: "Our team lives here. We don't chase storms across the country; we serve our neighbors. We're here to honor our warranties years down the road.",
                  },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
                      <Shield className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                      <p className="text-primary-foreground/70">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 md:py-28 bg-muted/50">
        <div className="container px-4">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Roofing Services Across Saskatchewan
            </h2>
            <p className="text-lg text-muted-foreground">
              Looking for a roofing company near you? We provide expert
              residential and commercial roofing services across the province.
              From swift repairs in Saskatoon to full replacements in Estevan,
              our local crews are ready to help.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              "Saskatoon",
              "Regina",
              "Moose Jaw",
              "Prince Albert",
              "Swift Current",
              "Yorkton",
              "Estevan",
              "Weyburn",
            ].map((city) => (
              <Link
                key={city}
                to={`/areas/${city.toLowerCase().replace(" ", "-")}`}
                className="flex items-center gap-2 p-4 bg-background rounded-lg border hover:border-primary hover:shadow-md transition-all group"
              >
                <MapPin className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                <span className="font-medium text-foreground">{city}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container px-4">
          <div className="text-center max-w-3xl mx-auto mb-12 space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Estimate Your Roof Cost
            </h2>
            <div className="bg-accent/10 border-l-4 border-accent p-4 rounded-r-lg text-left my-6">
              <p className="text-foreground font-medium">
                Most Saskatoon and Regina homeowners pay $6,000-$15,000 for an
                asphalt shingle roof replacement. Metal roofing runs
                $15,000-$30,000+. Repairs typically cost $300-$3,000.
              </p>
            </div>
            <p className="text-lg text-muted-foreground">
              Use our free calculator to get a personalized estimate, or read
              our{" "}
              <Link
                to="/roofing-cost-guide-saskatchewan"
                className="text-accent hover:underline font-medium"
              >
                Saskatchewan Roofing Cost Guide
              </Link>{" "}
              for full details. The estimate below is for a typical Saskatchewan
              home. Actual pricing depends on your specific roof's pitch,
              accessibility, and decking condition. Get a firm quote with our{" "}
              <Link
                to="/contact"
                className="text-accent hover:underline font-medium"
              >
                free inspection
              </Link>
              .
            </p>
          </div>
          <Calculator />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-muted/50">
        <div className="container px-4 max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <Accordion
            type="single"
            collapsible
            className="w-full bg-background rounded-xl border p-2 shadow-sm"
          >
            {[
              {
                q: "How much does a new roof cost in Saskatchewan?",
                a: "Most Saskatoon and Regina homeowners pay $6,000-$15,000 for an asphalt shingle roof replacement on a typical home. Metal roofing runs $15,000-$30,000+. Repairs range $300-$3,000. Use our cost calculator for a personalized estimate, or book a free inspection for a firm quote.",
              },
              {
                q: "How do I know if I need a roof repair or full replacement?",
                a: "Watch for these 5 signs: curling shingle edges, missing shingles after a storm, water stains on your ceiling, a roof older than 20 years, or excessive granule loss in your gutters. We offer free inspections to give you an honest assessment.",
              },
              {
                q: "How long does a roof replacement take?",
                a: "Most residential roof replacements are completed in 1 to 2 days, weather permitting. We prioritize efficiency without compromising quality.",
              },
              {
                q: "What warranty comes with a new roof?",
                a: "We provide strong 5-10 year workmanship warranties on our installations, in addition to the manufacturer's warranty on the materials (often 25-50 years). Because we are local, we are here to honor them.",
              },
              {
                q: "Does insurance cover hail damage?",
                a: "If your roof was damaged by a storm, your homeowner's insurance typically covers the replacement. We can help document the damage to assist with your claim.",
              },
              {
                q: "Do you offer financing for roof replacement?",
                a: "Yes, we offer flexible payment plans to help you spread the cost of your new roof over time. Ask us about our financing options during your free inspection.",
              },
            ].map((faq, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-b-0 px-4"
              >
                <AccordionTrigger className="text-left text-lg font-medium hover:no-underline hover:text-primary py-6">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base pb-6 leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="text-center mt-8">
            <Button variant="link" asChild className="text-primary font-bold">
              <Link to="/faq">
                View All FAQs <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-primary text-primary-foreground text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent"></div>
        <div className="container px-4 relative z-10 max-w-3xl mx-auto space-y-8">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
            Ready to Protect Your Home?
          </h2>
          <p className="text-xl text-primary-foreground/80">
            Still have questions about your specific roof? Every quote is
            fixed-price, in writing, with no surprise fees. We'll inspect,
            diagnose honestly, and recommend only what you actually need —
            repair, replace, or just monitor.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-6">
            <Button
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6 h-auto"
              asChild
            >
              <Link to="/contact">Get Your Free Roof Inspection</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-white/10 text-white border-white/20 hover:bg-white/20 text-lg px-8 py-6 h-auto"
              asChild
            >
              <a href="tel:6397397969">Call (639) 739-7969</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
